import { Inject, Injectable } from '@angular/core';
import {
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  tap,
} from 'rxjs/operators';
import { AccountDetailsResponse } from '../models/account.interface';
import { ANGULAR_HTTP_CLIENT_TOKEN } from '../angular-http-client.module';
import { UserService } from './user.service';

/**
 * Account data structure
 */
export interface Account {
  accountId: string; // Unique account identifier
  createdAt: string; // ISO date when account was created
}

/**
 * @alias AccountService
 * @description Service for managing account-related data and operations.
 * Provides reactive streams that auto-update when user account changes.
 *
 * State is held in private BehaviorSubjects so the current value is always
 * synchronously available. The initial value is explicitly `undefined` to
 * signal "not yet known"; consumers must filter undefined out before using.
 */
@Injectable()
export class AccountService {
  private static readonly TYPE: string = 'AccountService';
  private readonly basePath: string = environment.apis.accounts;

  private readonly _accountId$ = new BehaviorSubject<string | undefined>(
    undefined
  );

  private readonly _account$ = new BehaviorSubject<Account | undefined>(
    undefined
  );

  /**
   * Stream of the current account ID.
   * Emits `undefined` until the account ID becomes known, then the actual id.
   * Consumers that need a concrete value MUST filter out `undefined`.
   */
  public readonly accountId$: Observable<string | undefined> =
    this._accountId$.asObservable();

  /**
   * Stream of complete account information.
   * Emits `undefined` until account details have been fetched, then the
   * full {@link Account}. Fetching is triggered as soon as the account id
   * becomes available and is cached in the subject.
   * Consumers that need a concrete value MUST filter out `undefined`.
   */
  public readonly account$: Observable<Account | undefined> =
    this._account$.asObservable();

  public constructor(
    @Inject(ANGULAR_HTTP_CLIENT_TOKEN) private readonly httpClient: HttpClient,
    @Inject(COMMON_TOKENS.LogService) private readonly logService: LogService,
    private readonly userService: UserService
  ) {
    // Root-provided singleton: no teardown needed, subscription lives for the
    // entire app lifetime alongside the underlying user$ stream.
    this.userService.user$
      .pipe(
        map((user) => user?.['https://etrusted.com']?.accountRef),
        distinctUntilChanged(),
        tap((accountId) => this._accountId$.next(accountId)),
        filter((accountId): accountId is string => !!accountId),
        switchMap((accountId) =>
          this.getAccountCreatedAtDate(accountId).pipe(
            map(({ createdAt }): Account => ({ accountId, createdAt })),
            catchError((error) => {
              this.logService.error(
                AccountService.TYPE,
                new Error(`account$ fetch failed: ${JSON.stringify(error)}`)
              );

              return of<Account | undefined>(undefined);
            })
          )
        ),
        tap((account) => this._account$.next(account)),
        catchError((error) => {
          this.logService.error(
            AccountService.TYPE,
            new Error(`account stream failed: ${JSON.stringify(error)}`)
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Makes HTTP request to get account details from the API.
   *
   * @param accountId - The account ID to look up
   * @returns Observable of account details response
   */
  public getAccountCreatedAtDate(
    accountId: string
  ): Observable<AccountDetailsResponse> {
    return this.httpClient
      .get<AccountDetailsResponse>(`${this.basePath}/accounts/${accountId}`)
      .pipe(
        catchError(error => this.handleError('Get Account Created At Date Failed', error)));
  }

  /**
   * Handles HTTP errors by logging and re-throwing them.
   *
   * @param message - Contextual message for the error
   * @param error - The caught error
   * @returns Observable that errors with the original error
   */
  private handleError(message: string, error: Error): Observable<never> {
    this.logService.error(
      AccountService.TYPE,
      new Error(`${message}: ${JSON.stringify(error)}`)
    );

    return throwError(() => error);
  }
}
